// "Looking for a match" panel. Four mode tiles (the Play screen's hero
// tiles) toggle what the player is open to. Each mode's own settings sit
// directly under its tile so it is obvious what a change applies to;
// Opponents are not a setting: the search stays within one tier for five
// minutes, then opens to anyone.
// The layout never changes size: a deselected mode's settings dim.
import { Component } from 'preact';
import { motion } from 'framer-motion';
import {
  ACCENT_GOLD, TEXT_BODY, VIGNETTE, RADIUS, innerRadius, panelCornerStyle,
  TEXTURED_PANEL_STYLE, GOLD_BTN, BEVELED_BTN, FourCorners, OrnamentalDivider,
} from '../lib/medievalTheme';
import { SECTION_LABEL_STYLE, HEADER_TITLE_STYLE } from './play/ModeSetupDialog';
import { PillTabs, PillToggles } from './ui/PillTabs';
import { getUnlockedSetOptions } from './CreateDraftModal';
import { CloseButton } from './ui/navButtons';
import ModeTile from './play/ModeTile';
import { GAME_MODES } from '../utils/arena/gameModes';
import { playUI, UI } from '../utils/arena/uiSounds';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { t, k } from '../localization/t';

const PRESS = 'transform 160ms cubic-bezier(0.23, 1, 0.32, 1)';
// One inset from the panel edge on every side, sized so a control touching
// a panel corner lands on the md radius every other button uses:
// inset = panel radius − md, and the concentric inner radius is then md.
const PANEL_RADIUS = RADIUS['3xl'];
const PAD = `calc(${PANEL_RADIUS} - ${RADIUS.md})`;
const CORNER_RADIUS = innerRadius(PANEL_RADIUS, PAD);
const FOOTER_BTN = { padding: '0.625cqw 1.25cqw', borderRadius: CORNER_RADIUS, transition: PRESS };

// Column order. 'match' is the instance key for constructed 1v1, so its
// tile is retitled.
const COLUMNS = [
  { key: 'draft' },
  { key: 'sealed' },
  { key: 'match' },
  { key: 'sandbox' },
].map((c) => {
  const base = GAME_MODES.find((m) => m.key === c.key);
  const mode = c.key === 'match' ? { ...base, title: 'Constructed', tagline: 'Your deck. Ranked or casual.' } : base;
  return { ...c, mode };
});

const STAKES = [{ value: 'ranked', label: k('Ranked') }, { value: 'casual', label: k('Casual') }, { value: 'either', label: k('Either') }];
const FORMATS = [{ value: 'constructed', label: k('Constructed') }, { value: 'peasant', label: k('Peasant') }, { value: 'either', label: k('Either') }];
const RULES = [{ value: 'manual', label: k('Manual') }, { value: 'automated', label: k('Automated') }, { value: 'either', label: k('Either') }];
const RULE_VALUES = ['manual', 'automated', 'either'];
// Seats at a limited table: a quick pair, a full pod, or whichever fills first.
const PODS = [{ value: '2', label: '2' }, { value: '4', label: '4' }, { value: 'either', label: k('Either') }];
const POD_VALUES = PODS.map((o) => o.value);

function Setting({ label, children }) {
  return (
    <div className="flex flex-col min-w-0" style={{ gap: '0.4cqw' }}>
      <span style={SECTION_LABEL_STYLE}>{label}</span>
      {children}
    </div>
  );
}

function CheckSeal({ on }) {
  return (
    <span
      aria-hidden
      className="absolute grid place-items-center rounded-full pointer-events-none"
      style={{
        top: '0.6cqw', right: '0.6cqw', width: '1.5cqw', height: '1.5cqw', zIndex: 5,
        background: on ? 'linear-gradient(180deg, #f1d58a 0%, #b8862e 100%)' : 'rgba(12,10,8,0.85)',
        border: `1px solid ${on ? 'rgba(255,235,180,0.7)' : 'rgba(212,168,67,0.35)'}`,
        boxShadow: on ? '0 0 10px rgba(212,168,67,0.55), inset 0 1px 0 rgba(255,255,255,0.35)' : 'inset 0 1px 2px rgba(0,0,0,0.6)',
        transform: on ? 'scale(1)' : 'scale(0.9)',
        transition: 'background 200ms ease, box-shadow 200ms ease, transform 200ms cubic-bezier(0.23, 1, 0.32, 1)',
      }}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke={on ? '#1a1408' : 'rgba(212,168,67,0.4)'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8cqw', height: '0.8cqw' }}>
        <path d="M5 12.5l4.5 4.5L19 7" />
      </svg>
    </span>
  );
}

export default class LookingForMatchDialog extends Component {
  constructor(props) {
    super(props);
    const p = props.prefs || {};
    const stored = new Set((p.modes?.length ? p.modes : ['draft', 'sealed', 'constructed']).map((m) => (m === 'constructed' ? 'match' : m)));
    const rules = RULE_VALUES.includes(p.rules) ? p.rules : (p.automations ? 'automated' : 'manual');
    // Pill-width labels, release order.
    const ORDER = ['beta', 'arthurian', 'gothic'];
    const SHORT = { arthurian: 'Arthurian' };
    const setOptions = getUnlockedSetOptions(props.profile)
      .map((o) => ({ ...o, label: SHORT[o.value] || o.label }))
      .sort((a, b) => ORDER.indexOf(a.value) - ORDER.indexOf(b.value));
    const allSets = setOptions.map((o) => o.value);
    const sets = {};
    for (const k of ['draft', 'sealed']) {
      const saved = (p.sets?.[k] || []).filter((v) => allSets.includes(v));
      sets[k] = saved.length ? saved : allSets;
    }
    const pod = {};
    for (const k of ['draft', 'sealed']) pod[k] = POD_VALUES.includes(p.pod?.[k]) ? p.pod[k] : 'either';
    this.state = {
      setOptions,
      sets,
      pod,
      modes: stored,
      stakes: ['ranked', 'casual', 'either'].includes(p.stakes) ? p.stakes : 'either',
      format: ['constructed', 'peasant', 'either'].includes(p.format) ? p.format : 'either',
      rules,
    };
  }

  componentDidMount() { this._esc = pushDismiss(() => this.props.onClose?.()); }
  componentWillUnmount() { removeDismiss(this._esc); }

  toggleMode = (key) => {
    this.setState((s) => {
      const modes = new Set(s.modes);
      if (modes.has(key)) modes.delete(key); else modes.add(key);
      return { modes };
    });
  };

  setSets = (key, value) => {
    this.setState((s) => ({ sets: { ...s.sets, [key]: value } }));
  };

  setPod = (key, value) => {
    this.setState((s) => ({ pod: { ...s.pod, [key]: value } }));
  };

  prefs() {
    const { modes, stakes, format, rules, sets, pod } = this.state;
    return { modes: [...modes].map((m) => (m === 'match' ? 'constructed' : m)), stakes, format, rules, sets, pod };
  }

  render() {
    const { onClose, onStart, onStop, active, error, sorceryCards } = this.props;
    const { modes, stakes, format, rules, sets, pod, setOptions } = this.state;
    const canStart = modes.size > 0;
    const localize = (opts) => opts.map((o) => ({ ...o, label: t(o.label) }));
    return (
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(2px)' }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <motion.div
          initial={{ opacity: 0, y: 12, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.24, ease: [0.23, 1, 0.32, 1] }}
          className="relative flex flex-col overflow-hidden"
          style={{ ...TEXTURED_PANEL_STYLE, borderRadius: PANEL_RADIUS, width: '68cqw' }}
        >
          <FourCorners />

          <div className="flex items-center" style={{ padding: `${PAD} ${PAD} 1cqw`, minHeight: `calc(${PAD} + 2.125cqw)` }}>
            <h2 style={HEADER_TITLE_STYLE}>{t('Looking for a match')}</h2>
          </div>
          <CloseButton onClick={onClose} style={panelCornerStyle(PANEL_RADIUS, PAD)} />
          <div style={{ padding: `0 ${PAD}` }}><OrnamentalDivider /></div>

          <div className="flex flex-col" style={{ padding: `1.25cqw ${PAD}`, gap: '1.25cqw' }}>
            {/* One rules choice for every mode: being fine with the engine
                in one mode means being fine with it anywhere. */}
            <div className="flex items-center justify-center" style={{ gap: '1cqw' }}>
              <span style={SECTION_LABEL_STYLE}>{t('Rules')}</span>
              <PillTabs tabs={localize(RULES)} value={rules} minCol="7cqw" height="2cqw" onChange={(v) => this.setState({ rules: v })} />
            </div>
            <div className="grid grid-cols-4 items-start" style={{ gap: '1cqw' }}>
              {COLUMNS.map((col, idx) => {
                const on = modes.has(col.key);
                return (
                  <motion.div
                    key={col.key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.32, delay: idx * 0.05, ease: [0.23, 1, 0.32, 1] }}
                    className="flex flex-col"
                    style={{ gap: '0.9cqw' }}
                  >
                    <div className="relative">
                      <ModeTile
                        mode={col.mode}
                        available
                        compact
                        selected={on}
                        badges={[]}
                        sorceryCards={sorceryCards}
                        onClick={() => this.toggleMode(col.key)}
                      />
                      <CheckSeal on={on} />
                    </div>
                    {/* This mode's terms. Dim, never removed, when the
                        mode is off so the columns keep their shape. */}
                    <div className="flex flex-col" style={{ gap: '0.75cqw', opacity: on ? 1 : 0.35, transition: 'opacity 200ms ease', pointerEvents: on ? undefined : 'none' }}>
                      {col.key === 'draft' || col.key === 'sealed' ? (
                        <>
                          <Setting label={t('Sets')}>
                            <PillToggles tabs={setOptions} value={sets[col.key]} height="2cqw" onChange={(v) => this.setSets(col.key, v)} />
                          </Setting>
                          <Setting label={t('Players')}>
                            <PillTabs tabs={localize(PODS)} value={pod[col.key]} fill height="2cqw" onChange={(v) => this.setPod(col.key, v)} />
                          </Setting>
                        </>
                      ) : null}
                      {col.key === 'match' ? (
                        <>
                          <Setting label={t('Stakes')}>
                            <PillTabs tabs={localize(STAKES)} value={stakes} fill height="2cqw" onChange={(v) => this.setState({ stakes: v })} />
                          </Setting>
                          <Setting label={t('Format')}>
                            <PillTabs tabs={localize(FORMATS)} value={format} fill height="2cqw" onChange={(v) => this.setState({ format: v })} />
                          </Setting>
                        </>
                      ) : null}
                    </div>
                  </motion.div>
                );
              })}
            </div>

          </div>

          <div style={{ padding: `0 ${PAD}` }}><OrnamentalDivider /></div>
          <div className="flex items-center gap-3" style={{ padding: `1cqw ${PAD} ${PAD}` }}>
            <span className="text-[0.62cqw] flex-1 min-w-0 truncate" style={{ color: '#e89090' }}>{error || ''}</span>
            {active ? (
              <button type="button" className="text-sm font-semibold cursor-pointer active:scale-[0.97]" style={{ ...BEVELED_BTN, color: ACCENT_GOLD, ...FOOTER_BTN }} onClick={() => { playUI(UI.CANCEL); onStop(); }}>
                {t('Stop looking')}
              </button>
            ) : (
              <button type="button" className="text-sm cursor-pointer active:scale-[0.97]" style={{ ...BEVELED_BTN, color: TEXT_BODY, ...FOOTER_BTN }} data-sound={UI.CANCEL} onClick={onClose}>
                {t('Cancel')}
              </button>
            )}
            <button
              type="button"
              disabled={!canStart}
              className="text-sm font-semibold cursor-pointer disabled:opacity-40 active:scale-[0.97]"
              style={{ ...GOLD_BTN, ...FOOTER_BTN }}
              onClick={() => { playUI(UI.CONFIRM); onStart(this.prefs()); }}
            >
              {active ? t('Update') : t('Start looking')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }
}
